/**
 * TDX 充電費字串 → 去重後的短表
 */

const BAND_ORDER = ['尖峰', '半尖峰', '離峰', '一般', '固定', '每日'];
const BAND_MATCH = ['半尖峰', '尖峰', '離峰', '一般', '固定', '每日'];

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function formatEvPrice(n) {
    if (!Number.isFinite(n)) return '';
    return String(Number(n.toFixed(2)))
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '');
}

function splitRateParts(raw) {
    return String(raw || '')
        .split(/[,，;；]\s*/)
        .map((s) => s.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
}

export function parseEvChargingRatePart(part) {
    const cleaned = String(part || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return null;
    const priceM = cleaned.match(/(\d+(?:\.\d+)?)\s*元/);
    const price = priceM ? Number(priceM[1]) : null;
    const unit = /每度/.test(cleaned) ? '度' : /每分/.test(cleaned) ? '分' : /每.?時/.test(cleaned) ? '時' : '';
    const billing = /計度/.test(cleaned) ? '計度' : /計時/.test(cleaned) ? '計時' : /計分/.test(cleaned) ? '計分' : '';
    const ac = /^AC\b/i.test(cleaned) ? 'AC' : /^DC\b/i.test(cleaned) ? 'DC' : '';
    let band = '';
    for (const b of BAND_MATCH) {
        if (cleaned.includes(b)) {
            band = b;
            break;
        }
    }
    const win = cleaned.match(/(\d{1,2}:\d{2})\s*[-~～到至]\s*(\d{1,2}:\d{2})/);
    const window = win ? `${win[1].padStart(5, '0')}-${win[2].padStart(5, '0')}` : '';
    return { billing, band, window, price, unit, ac, raw: cleaned };
}

function isAllDay(window) {
    return window === '00:00-23:59' || window === '00:00-24:00';
}

export function parseEvChargingRates(raw) {
    const seen = new Set();
    const parts = [];
    for (const chunk of splitRateParts(raw)) {
        const row = parseEvChargingRatePart(chunk);
        if (!row || row.price == null) continue;
        const key = [row.billing, row.band, row.window, row.price, row.unit, row.ac].join('|');
        if (seen.has(key)) continue;
        seen.add(key);
        parts.push(row);
    }

    const groups = new Map();
    for (const row of parts) {
        const gkey = [row.billing, row.band, row.price, row.unit, row.ac].join('|');
        if (!groups.has(gkey)) {
            groups.set(gkey, { ...row, windows: [] });
        }
        if (row.window) groups.get(gkey).windows.push(row.window);
    }

    const collapsed = [...groups.values()].map((g) => {
        const specific = g.windows.filter((w) => !isAllDay(w));
        const windows = specific.length ? [...new Set(specific)] : [...new Set(g.windows)];
        return {
            billing: g.billing,
            band: g.band,
            price: g.price,
            unit: g.unit,
            ac: g.ac,
            windows: windows.length > 1 ? [] : windows
        };
    });

    const nonzero = collapsed.filter((g) => g.price > 0);
    const usable = nonzero.length ? nonzero : collapsed;
    const prices = [...new Set(usable.map((g) => g.price))];
    const units = [...new Set(usable.map((g) => g.unit).filter(Boolean))];
    if (prices.length === 1 && units.length <= 1) {
        return [
            {
                billing: usable[0].billing,
                band: '固定',
                price: prices[0],
                unit: units[0] || usable[0].unit,
                ac: usable[0].ac,
                windows: []
            }
        ];
    }

    usable.sort((a, b) => {
        const ai = BAND_ORDER.indexOf(a.band);
        const bi = BAND_ORDER.indexOf(b.band);
        return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi) || a.price - b.price;
    });
    return usable;
}

function unitSuffix(unit) {
    if (unit === '度') return '元／度';
    if (unit === '分') return '元／分';
    if (unit === '時') return '元／時';
    return '元';
}

export function resolveLinkedEvChargingRates(linked, lookupByUid) {
    const items = [];
    for (const s of linked || []) {
        let raw = String(s?.charging_rate || s?.h_charging_rate || '').trim();
        if (!raw && typeof lookupByUid === 'function' && s?.station_uid) {
            raw = String(lookupByUid(s.station_uid) || '').trim();
        }
        items.push({
            name: s?.name || s?.station_id || '',
            station_uid: s?.station_uid || '',
            rate: raw
        });
    }
    return items;
}

export function parkingLinkedEvFeeHtml(linked, lookupByUid) {
    const items = resolveLinkedEvChargingRates(linked, lookupByUid);
    if (!items.length) return '';
    const withRate = items.filter((i) => i.rate);
    if (!withRate.length) {
        return `<div class="park-fee-empty">場內充電站未提供費率</div>`;
    }
    const byRate = new Map();
    for (const i of withRate) {
        if (!byRate.has(i.rate)) byRate.set(i.rate, []);
        byRate.get(i.rate).push(i.name);
    }
    if (byRate.size === 1) {
        return evChargingRateTableHtml([...byRate.keys()][0]);
    }
    return [...byRate.entries()]
        .map(([rate, names]) => {
            const label = names.find(Boolean) || '';
            const head = label
                ? `<div class="park-fee-ev-station">${escapeHtml(label)}</div>`
                : '';
            return `${head}${evChargingRateTableHtml(rate)}`;
        })
        .join('');
}

export function evChargingRateTableHtml(raw) {
    const rows = parseEvChargingRates(raw);
    if (!rows.length) {
        const text = String(raw || '').trim();
        return text
            ? `<div class="park-fee-empty">此來源未提供可判讀費率</div>`
            : '';
    }
    const body = rows
        .map((r) => {
            const label = [r.ac, r.band || r.billing || '充電'].filter(Boolean).join(' ');
            const win = r.windows[0] ? `<span class="park-fee-win">${escapeHtml(r.windows[0])}</span>` : '';
            return `<div class="park-fee-row park-fee-row-ev">
            <div class="park-fee-veh"><span class="park-ico park-ico-ev" title="充電費">⚡</span><span class="park-fee-veh-name">${escapeHtml(label)}</span></div>
            <div class="park-fee-rates"><span class="park-fee-amt">${escapeHtml(formatEvPrice(r.price))} ${unitSuffix(r.unit)}</span>${win}</div>
        </div>`;
        })
        .join('');
    return `<div class="park-fee-table">${body}</div>`;
}
